/** Pure helpers for project folder tree UI (from folder selector design). */

export function buildFolderTree(flatFolders, projectId) {
  if (!flatFolders?.length || !projectId) return [];

  const filtered = flatFolders.filter(
    (row) => row?.project?.id === projectId || row?.project_id === projectId
  );
  const byId = new Map();

  filtered.forEach((row) => {
    byId.set(row.id, {
      id: row.id,
      name: row.title ?? row.displayTitle ?? "",
      access: row.effective_access || row.access || "project",
      children: [],
      object: row,
    });
  });

  const roots = [];
  filtered.forEach((row) => {
    const node = byId.get(row.id);
    const parentId = row?.parent?.id ?? row?.parent_id ?? null;
    if (parentId && byId.has(parentId)) {
      byId.get(parentId).children.push(node);
    } else {
      roots.push(node);
    }
  });

  return roots;
}

export function findNode(nodes, id, parent = null, depth = 0) {
  if (id == null || id === "") return null;
  const want = String(id);
  for (const n of nodes) {
    if (String(n.id) === want) return { node: n, parent, depth };
    if (n.children?.length) {
      const hit = findNode(n.children, id, n, depth + 1);
      if (hit) return hit;
    }
  }
  return null;
}

export function ancestorPath(nodes, id, trail = []) {
  if (id == null || id === "") return null;
  const want = String(id);
  for (const n of nodes) {
    if (String(n.id) === want) return trail;
    if (n.children?.length) {
      const hit = ancestorPath(n.children, id, [...trail, n.id]);
      if (hit) return hit;
    }
  }
  return null;
}

export function namePath(nodes, id) {
  const hit = findNode(nodes, id);
  if (!hit) return [];
  const anc = ancestorPath(nodes, id) || [];
  return [
    ...anc.map((aid) => findNode(nodes, aid)?.node?.name).filter(Boolean),
    hit.node.name,
  ];
}

export function isDescendant(nodes, id, maybeAncestorId) {
  if (maybeAncestorId == null || maybeAncestorId === "") return false;
  const anc = ancestorPath(nodes, id) || [];
  const want = String(maybeAncestorId);
  return anc.some((aid) => String(aid) === want);
}

export function countDescendants(node) {
  if (!node.children?.length) return 0;
  let c = node.children.length;
  for (const ch of node.children) c += countDescendants(ch);
  return c;
}

export function collectSubtreeIds(node) {
  if (!node?.id) return [];
  const ids = [node.id];
  for (const child of node.children || []) {
    ids.push(...collectSubtreeIds(child));
  }
  return ids;
}

export function flatten(nodes, expanded, depth = 0, out = []) {
  for (const n of nodes) {
    const hasKids = n.children?.length > 0;
    out.push({ node: n, depth, hasKids });
    if (hasKids && expanded.has(n.id)) {
      flatten(n.children, expanded, depth + 1, out);
    }
  }
  return out;
}

/** Search-aware row list (auto-expands matching branches). */
export function searchRows(tree, query) {
  const q = query.trim().toLowerCase();
  if (!q) return null;

  const out = [];
  const walk = (nodes, depth) => {
    for (const n of nodes) {
      const selfMatch = n.name.toLowerCase().includes(q);
      const kids = n.children || [];
      const childRows = [];
      const childWalk = (cn, cd, sink) => {
        for (const c of cn) {
          const cm = c.name.toLowerCase().includes(q);
          const grand = [];
          childWalk(c.children || [], cd + 1, grand);
          if (cm || grand.length) {
            sink.push({
              node: c,
              depth: cd,
              hasKids: (c.children || []).length > 0,
              matched: cm,
            });
            sink.push(...grand);
          }
        }
      };
      childWalk(kids, depth + 1, childRows);
      if (selfMatch || childRows.length) {
        out.push({ node: n, depth, hasKids: kids.length > 0, matched: selfMatch });
        out.push(...childRows);
      }
    }
  };
  walk(tree, 0);
  return out;
}
